import { Pagination } from "@/components/Pagination";
import {
  PROJECTS_TAB,
  ProjectSecondLevelHeader,
} from "@/features/components/ProjectSecondLevelHeader";
import { useModals } from "@/features/modals/hooks";
import { ActiveProjectProvider } from "@/features/project/ActiveProject";
import { useAiFeatureConfig } from "@/features/projects/hooks/useAiFeatureConfig";
import { useShowSubHeader } from "@/hooks/useSubHeaderVisibility";
import { useTitle } from "@/hooks/useTitle";
import { DynamicSkeletonRows, LightMode } from "@repo/skapa";
import {
  PaddedContainer,
  SectionContainer,
  openNewProjectModal,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { setActiveProject } from "../project/setActiveProject";
import { ProjectsPageSkeleton } from "./ProjectsPageSkeleton";
import { ProjectsTable } from "./ProjectsTable";
import { useSearchProject, useSearchProjectFilters } from "./SearchProject";
import { ProjectsFilter } from "./filter/Filter";
import { GenerateProjectsModal } from "./modals/GenerateProjectsModal";
import { Quickview } from "./modals/Quickview";
import { PortfolioSummary } from "./portfolioSummary/PortfolioSummary";

export const ProjectsPage = () => {
  useTitle("Projects");
  useSearchProjectFilters();

  setActiveProject(null, true);

  const {
    result,
    totalSize,
    size,
    setSize,
    options,
    setOptions,
    actions,
    projectFilters,
    isValidating,
  } = useSearchProject();

  const navigate = useNavigate();
  const { pushModal } = useModals();
  const {
    isSubHeaderVisible,
    isValidating: isSubHeaderValidating,
    isLoading: isSubHeaderLoading,
  } = useShowSubHeader();

  const [quickviewId, setQuickviewId] = useState<number | undefined>(undefined);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [isGenerateProjectsModalOpen, setIsGenerateProjectsModalOpen] =
    useState(false);
  useEffect(() => {
    if (projectFilters.isLoaded) {
      setShowSkeleton(false);
    }
  }, [projectFilters.isLoaded]);

  const onSelectProject = (projectId: number) => {
    navigate(`/opportunity/${projectId}/`);
    trackAnalytics("manage_project");
  };

  const onProjectCreated = (projectId: number) => {
    navigate(`/opportunity/${projectId}/`);
    trackAnalytics("new_project_created");
  };

  const showCoworkerPortfolioSummary = useAiFeatureConfig({
    feature: "coworkerPortfolioSummary",
    buCodes: projectFilters.filters.businessUnit,
  });

  return (
    <>
      {!isSubHeaderValidating && !isSubHeaderLoading && !isSubHeaderVisible && (
        <ProjectSecondLevelHeader activeTabPanelId={PROJECTS_TAB} />
      )}
      <div role="tabpanel" id={`${PROJECTS_TAB}_TAB`}>
        <PaddedContainer backgroundColor={LightMode.colourNeutral1}>
          {showSkeleton ? (
            <ProjectsPageSkeleton />
          ) : (
            <ProjectsFilter
              filters={projectFilters.filters}
              actions={actions}
              resultSize={totalSize}
              options={options}
              onSort={(sortKey, sortOrder) => {
                setOptions((prev) => ({
                  ...prev,
                  sortKey,
                  sortOrder,
                  pageIndex: 0,
                }));
              }}
              openNewOpportunity={() => {
                openNewProjectModal({
                  onSelectProject,
                  onProjectCreated,
                  pushModal,
                  resetModal: true,
                });
              }}
              openGenerateProjectsModal={() =>
                setIsGenerateProjectsModalOpen(true)
              }
            />
          )}

          <SectionContainer>
            {showCoworkerPortfolioSummary && (
              <PortfolioSummary
                totalSize={totalSize}
                filters={projectFilters.filters}
                options={options}
                actions={actions}
              />
            )}
            <ProjectsTable
              content={result}
              showQuickview={setQuickviewId}
              options={options}
              setOptions={setOptions}
              filters={projectFilters.filters}
            />
            {isValidating && (
              <DynamicSkeletonRows
                numberOfRows={5}
                height="40px"
                width="100%"
              />
            )}
            {!isValidating && (
              <Pagination
                setSize={setSize}
                size={size}
                totalNumber={totalSize ?? 0}
                currentNumber={result.length}
                pageSize={options.pageSize}
                onPageSizeChange={(newPageSize) => {
                  setOptions((prev) => ({ ...prev, pageSize: newPageSize }));

                  setSize(1);
                }}
              />
            )}
          </SectionContainer>
        </PaddedContainer>

        <ActiveProjectProvider opportunityId={quickviewId}>
          <Quickview
            opportunityId={quickviewId}
            closeQuickView={() => setQuickviewId(undefined)}
          />
        </ActiveProjectProvider>

        <GenerateProjectsModal
          isOpen={isGenerateProjectsModalOpen}
          onClose={() => setIsGenerateProjectsModalOpen(false)}
          filters={projectFilters.filters}
          options={options}
        />
      </div>
    </>
  );
};
